import { type ApolloError, useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PUBLISH_ERROR_I18N_KEYS } from "../constants/publishErrors";
import {
  GetEventProxyDocument,
  useArchiveEventMutation,
  useDuplicateEventMutation,
  usePublishEventMutation,
  useResumeTicketSalesMutation,
  useStopTicketSalesMutation,
} from "../graphql/event.hooks";
import {
  archiveModalVar,
  attetionModalVar,
  createCopyModalVar,
  resumeTicketsSaleModalVar,
  stopTicketsSaleModalVar,
} from "../graphql/globalVar/state";
import { REFETCH_QUERIES } from "../pages/myEventsPage/constants";
import { notificationService } from "../services/notificationService";

import useGetUrlLocale from "./useGetUrlLocale";

export function useEventActions() {
  const { t } = useTranslation();
  const client = useApolloClient();
  const navigate = useNavigate();
  const locale = useGetUrlLocale();

  const getValidationErrorMessage = (
    code: keyof typeof PUBLISH_ERROR_I18N_KEYS
  ): string =>
    t(PUBLISH_ERROR_I18N_KEYS[code] ?? "events.publishErrors.fallback", {
      defaultValue: t("events.publishErrors.fallback"),
    });

  const [archiveEventMutation, { loading: archiveLoading }] =
    useArchiveEventMutation({ refetchQueries: REFETCH_QUERIES });

  const [stopTicketSalesMutation, { loading: stopLoading }] =
    useStopTicketSalesMutation({ refetchQueries: REFETCH_QUERIES });

  const [resumeTicketSalesMutation, { loading: resumeLoading }] =
    useResumeTicketSalesMutation({ refetchQueries: REFETCH_QUERIES });

  const [duplicateEventMutation, { loading: duplicateLoading }] =
    useDuplicateEventMutation({ refetchQueries: REFETCH_QUERIES });

  const [publishEventMutation, { loading: publishLoading }] =
    usePublishEventMutation({ refetchQueries: REFETCH_QUERIES });

  const archiveEvent = async (eventId: number) => {
    try {
      await archiveEventMutation({ variables: { eventId } });
      archiveModalVar(null);
      notificationService.success({ msg: t("events.toasts.archived") });
    } catch {
      notificationService.danger({ msg: t("events.toasts.archiveFailed") });
    }
  };

  const stopTicketSales = async (eventId: number) => {
    try {
      await stopTicketSalesMutation({ variables: { eventId } });
      stopTicketsSaleModalVar(null);
      notificationService.success({ msg: t("events.toasts.salesStopped") });
    } catch {
      notificationService.danger({ msg: t("events.toasts.salesStopFailed") });
    }
  };

  const resumeTicketSales = async (eventId: number) => {
    try {
      await resumeTicketSalesMutation({ variables: { eventId } });
      resumeTicketsSaleModalVar(null);
      notificationService.success({ msg: t("events.toasts.salesResumed") });
    } catch {
      notificationService.danger({ msg: t("events.toasts.salesResumeFailed") });
    }
  };

  const publishEvent = async (eventId: string) => {
    try {
      const result = await publishEventMutation({
        variables: { eventId: Number(eventId) },
      });
      const validationErrors = result.data?.publishEvent ?? [];

      if (validationErrors.length === 0) {
        attetionModalVar(null);
        notificationService.success({ msg: t("events.toasts.published") });
        void client
          .refetchQueries({ include: [GetEventProxyDocument] })
          .catch(() => undefined);
      } else {
        notificationService.danger({
          msg: getValidationErrorMessage(validationErrors[0]),
        });
      }
    } catch (err) {
      const apolloError = err as ApolloError;
      const ext = apolloError.graphQLErrors[0]?.extensions as unknown as
        | string[]
        | undefined;
      if (ext?.[0]?.startsWith("event.edit.")) {
        attetionModalVar(null);
        createCopyModalVar(Number(eventId));
      } else {
        notificationService.danger({
          msg: t("events.publishErrors.fallback"),
        });
      }
    }
  };

  const createCopy = async (eventId: number) => {
    try {
      const result = await duplicateEventMutation({ variables: { eventId } });
      const newEventId = result.data?.duplicateEvent.id;
      createCopyModalVar(null);
      notificationService.success({ msg: t("events.toasts.copyCreated") });
      if (newEventId) {
        navigate(`${locale}/dashboard/${newEventId}`);
      }
    } catch {
      notificationService.danger({ msg: t("events.toasts.copyFailed") });
    }
  };

  return {
    archiveEvent,
    stopTicketSales,
    resumeTicketSales,
    publishEvent,
    createCopy,
    archiveLoading,
    stopLoading,
    resumeLoading,
    publishLoading,
    duplicateLoading,
  };
}
